import { t } from "@lingui/core/macro";
import { clsx } from "@regrello/core-utils";
import {
  type RegrelloIconName,
  type RegrelloIntent,
  type RegrelloTooltipProps,
  RegrelloTooltippedInfoIcon,
  RegrelloTypography,
} from "@regrello/ui-core";
import React from "react";

import { DETAIL_DIALOG_FORM_FIELD_WIDTH_PX } from "../../../../../utils/dialogSettings";

export interface RegrelloFormLabelProps {
  /** An adornment to display at the end of the field after the help icon and before the "(Deleted)" label. */
  endAdornment?: React.ReactNode;

  /**
   * Where the `endAdornment` should appear along the label row.
   *
   * - `"inline"`: The `endAdornment` will appear inline immediately after the label.
   * - `"end"`: The `endAdornment` will align to the right edge of the row the label is on.
   *
   * @default "inline"
   */
  endAdornmentPosition?: "inline" | "end";

  htmlFor?: string;

  /**
   * The name of the tooltip triggering icon
   */
  infoTooltipIconName?: RegrelloIconName;

  /**
   * The intent to apply to this icon.
   *
   * @default undefined
   */
  infoTooltipIntent?: RegrelloIntent;

  /**
   * If defined, an info icon will be rendered to the right of the label with this text as tooltip
   * content.
   */
  infoTooltipText?: string;

  /**
   * The visual treatment to give to the tooltip.
   * - `"default"`: A standard dark-background tooltip.
   * - `"popover"`: A light-background tooltip with a border.
   *
   * @default "default"
   */
  infoTooltipVariant?: RegrelloTooltipProps["variant"];

  /**
   * Whether to zero out default margins around the form field label.
   *
   * @default false
   */
  isDefaultMarginsOmitted?: boolean;

  /** Whether to show a tooltiped deleted badge besides the label. */
  isDeleted?: boolean;

  isLabelVerticallyCentered?: boolean;

  isRequiredAsteriskShown?: boolean;

  label: React.ReactNode;

  /**
   * How to align the label text in relation to the form field.
   */
  labelAlignment?: "left" | "center" | "right";

  /** The hardcoded width of the label, passed as an inline `style`. */
  labelWidth?: number | string;

  /**
   * Callback invoked when the label is clicked.
   */
  onClick?: () => void;

  /** Whether the form field is rendered by a spectrum form. */
  variant?: "default" | "spectrum";
}

export const RegrelloFormLabel = React.memo<RegrelloFormLabelProps>(function RegrelloFormLabelFn({
  endAdornment,
  endAdornmentPosition = "inline" as const,
  htmlFor,
  infoTooltipText,
  infoTooltipIconName = "info",
  infoTooltipVariant = "default",
  infoTooltipIntent,
  isDeleted = false,
  isDefaultMarginsOmitted = false,
  isRequiredAsteriskShown = false,
  isLabelVerticallyCentered = false,
  label,
  labelAlignment = "left",
  labelWidth,
  onClick,
  variant = "default",
}) {
  return (
    <div
      className="flex flex-none items-start gap-2 pt-2"
      style={{ width: labelWidth ?? DETAIL_DIALOG_FORM_FIELD_WIDTH_PX }}
    >
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <label
        className={clsx("flex text-sm", {
          "text-center": labelAlignment === "center",
          "text-left": labelAlignment === "left",
          "text-right": labelAlignment === "right",
        })}
        htmlFor={htmlFor}
        // Give the label a predictable ID so that any input can reference it via aria-labelledby.
        // Particularly important for RegrelloNumericInput, which uses the accessibility-conscious React
        // Aria library.
        id={htmlFor != null ? `${htmlFor}-label` : undefined}
        onClick={onClick}
      >
        <div
          // (clewis): Need to set an explicit color to avoid getting grayed out by FormLabel styles.
          className={clsx("flex", {
            "mt-2.25": isLabelVerticallyCentered && !isDefaultMarginsOmitted,
            "text-textDefault flex items-start": variant === "default",
            "font-normal text-textMuted": variant === "spectrum",
          })}
        >
          <span
            className={clsx("flex items-start font-semibold hyphens-auto break-words gap-1", {
              "font-normal": variant === "spectrum",
            })}
          >
            {label}
            {isRequiredAsteriskShown && <span className="text-danger-textMuted">*</span>}
          </span>
        </div>
      </label>

      <RegrelloTooltippedInfoIcon
        className="mt-0.5"
        iconName={infoTooltipIconName}
        intent={infoTooltipIntent}
        tooltipText={infoTooltipText}
        variant={infoTooltipVariant}
      />

      {/* (clewis): Clamp to the same line height as the label to ensure there is no spacing jitter when the end adornment shows and hides. */}
      {endAdornment != null && endAdornmentPosition === "inline" && <div className="flex-none h-5">{endAdornment}</div>}

      {isDeleted && (
        <RegrelloTypography className="inline-flex items-center ml-1 flex-none" muted={true}>
          {`(${t`Deleted`})`}
          <RegrelloTooltippedInfoIcon
            iconName="info"
            tooltipText={t`This field has been deleted from your workspace. We recommend using a different field instead.`}
            variant="popover"
          />
        </RegrelloTypography>
      )}

      {endAdornment != null && endAdornmentPosition === "end" && (
        <>
          <div className="flex-auto" />
          <div className="flex-none h-5">{endAdornment}</div>
        </>
      )}
    </div>
  );
});
